import { AlertTriangle, Clock, Cpu, MapPin, Radio, Siren } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import type { ConfigurationProfileSchema } from '../../../../schemas/production/configuration-profile.schema';
import { Alert, AlertDescription } from '../../../ui/alert';
import { Badge } from '../../../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../ui/form';
import { Input } from '../../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Separator } from '../../../ui/separator';
import { Switch } from '../../../ui/switch';

// Modos de trabalho (WFOM) — manual BWS LL NB, seção "Modos de trabalho".
const WORK_MODES = [
  { value: '2', label: 'Viagem (Normal)', description: 'Respeita os tempos de movimento e parado' },
  { value: '1', label: 'Estoque', description: 'Posição a cada 6 horas' },
  { value: '128', label: 'Desabilitado', description: 'Posição a cada 24 horas' },
] as const;

// Flags "z" do WBTR — manual BWS LL NB, seção "Comportamento do Rádio".
const RADIO_FLAGS = [
  { value: '37', label: 'Enviar somente quando jammer detectado' },
  { value: '41', label: 'Enviar apenas quando sem cobertura celular' },
  { value: '45', label: 'Enviar quando jammer detectado e sem cobertura de celular' },
] as const;

export function SpecificBwsLlNbConfigurationProfileForm() {
  const { control } = useFormContext<ConfigurationProfileSchema>();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Cpu className="h-5 w-5" />
          Configurações Específicas - BWS LL NB
        </CardTitle>
        <CardDescription>
          Configure parâmetros específicos do equipamento BWS LL NB IoT+LoRa
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Intervalos de transmissão */}
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <h4 className="font-medium">Intervalo de Transmissão</h4>
            <Badge variant="outline" className="text-xs">
              Minutos
            </Badge>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormField
                control={control}
                name="config.specific.data_transmission_moving"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Monitorado Ligado (min)</FormLabel>
                    <FormControl>
                      <Input placeholder="5" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormDescription>Em movimento. 1 à 65535, padrão 5.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.data_transmission_stopped"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Monitorado Desligado (min)</FormLabel>
                    <FormControl>
                      <Input placeholder="180" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormDescription>Parado. 1 à 65535.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.data_transmission_disabled"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Modo desabilitado (min)</FormLabel>
                    <FormControl>
                      <Input placeholder="1440" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormDescription>Fora de uso. 0 à 65535, padrão 1440.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.data_transmission_stock"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Modo estoque (min)</FormLabel>
                    <FormControl>
                      <Input placeholder="360" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormDescription>Tempo em modo estoque.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Modo de trabalho e GPS */}
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            <h4 className="font-medium">Modo de Trabalho e GPS</h4>
          </div>

          <div className="rounded-lg border bg-card p-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={control}
                name="config.specific.work_mode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Modo de trabalho</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(Number(value))}
                      value={field.value?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um modo de trabalho" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {WORK_MODES.map((mode) => (
                          <SelectItem key={mode.value} value={mode.value}>
                            {mode.label} — {mode.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.gps_fix_timeout"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tempo aguardando FIX (seg)</FormLabel>
                    <FormControl>
                      <Input placeholder="120" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormDescription>
                      Só tem efeito com GPS em cada ciclo habilitado. Padrão 120.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={control}
                name="config.specific.gps_every_cycle"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Ligar GPS em cada ciclo</FormLabel>
                      <FormDescription>Habilita o GPS sempre que for posicionar.</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.power_string"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">String de potência</FormLabel>
                      <FormDescription>Permite o envio da string de potência.</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* SOS */}
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <Siren className="h-4 w-4" />
            <h4 className="font-medium">Modo SOS (Emergência)</h4>
          </div>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              O modo SOS liga GNSS e rádio LoRa e reduz o intervalo de envio, aumentando o consumo
              de bateria. A ativação é feita pela plataforma (ou comando WFGM) apenas quando
              necessário.
            </AlertDescription>
          </Alert>

          <div className="rounded-lg border bg-card p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={control}
                name="config.specific.data_transmission_sos"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tempo de report em SOS (seg)</FormLabel>
                    <FormControl>
                      <Input placeholder="60" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormDescription>0 à 65535, padrão 60.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.sos_timeout"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Time out de SOS (min)</FormLabel>
                    <FormControl>
                      <Input placeholder="60" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormDescription>SOS temporizado. Máximo 65535, padrão 60.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Comportamento do rádio */}
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <Radio className="h-4 w-4" />
            <h4 className="font-medium">Comportamento do Rádio</h4>
            <Badge variant="outline" className="text-xs">
              LoRaWAN / P2P
            </Badge>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground mb-4">
              Preencha todos os campos para enviar o comando WBTR, ou deixe todos vazios para manter
              a configuração atual.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <FormField
                control={control}
                name="config.specific.radio_behavior.no_connection_timeout"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tempo sem conexão (min)</FormLabel>
                    <FormControl>
                      <Input placeholder="1" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormDescription>Antes de entrar no LoRa. Padrão 1.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.radio_behavior.stopped_cycle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ciclo LoRa parado</FormLabel>
                    <FormControl>
                      <Input placeholder="3" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormDescription>1 envio LoRa a cada x tentativas. Padrão 3.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.radio_behavior.moving_cycle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ciclo LoRa em movimento</FormLabel>
                    <FormControl>
                      <Input placeholder="3" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormDescription>1 envio LoRa a cada y tentativas. Padrão 3.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.radio_behavior.flags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quando enviar por LoRa</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(Number(value))}
                      value={field.value?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {RADIO_FLAGS.map((flag) => (
                          <SelectItem key={flag.value} value={flag.value}>
                            {flag.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
